import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { markRoles, type SchmancyMarkColor, type SchmancyTone } from '@schmancy/theme/tone'
import { css, html } from 'lit'
import { property } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { repeat } from 'lit/directives/repeat.js'
import { when } from 'lit/directives/when.js'
import '@schmancy/primitives/tone-dot'
import '@schmancy/primitives/typography'

/** One part of the whole. `value` is absolute; the share is derived against
 *  the sum of every segment, so a percentage is never passed in. */
export interface SegmentedBarSegment {
	/** Stable identity for the keyed render. */
	id: string
	label: string
	value: number
	/**
	 * Pins this segment to a semantic tone instead of its place in the theme's
	 * categorical scale. Reach for it only when the segment genuinely reports a
	 * state (a refusal, a success); a categorical split — which event, which
	 * retrieval channel — wants the scale, whose members are peers.
	 */
	color?: SchmancyTone
}

/** Gap between two adjacent segments, in px. Constant, so it comes out of the
 *  free space before it is distributed and every segment's width stays exactly
 *  proportional to its own value.
 *
 *  It is also the ONLY thing separating two segments that share a tone, which
 *  is a deliberate case rather than an edge: a tone is a reading, so three
 *  segments all reporting one refusal are one band of that colour and the key
 *  beneath breaks them apart. Drive the colour separation to zero and this
 *  number is the whole boundary, which is why `segmented-bar.test.ts` pins the
 *  PAINTED gap in the same test that pins the sameness. `<schmancy-funnel>`
 *  answers the same question the other way and says so at its own connector:
 *  its wedges abut the trapezoid with no spacer at all, because the funnel's
 *  claim is one continuous silhouette and a gap inside it would read as a
 *  break in the cohort rather than as a boundary between two members. */
const SEGMENT_GAP_PX = 2

interface SegmentedBarPart {
	segment: SegmentedBarSegment
	index: number
	share: number
	/** The tone or series index this part paints in — one value, so the track
	 *  and the legend's own mark can never drift apart. */
	color: SchmancyMarkColor
}

export interface SchmancySegmentActivateDetail {
	id: string
	segment: SegmentedBarSegment
	index: number
	originalEvent: MouseEvent
}

export type SchmancySegmentActivateEvent = CustomEvent<SchmancySegmentActivateDetail>

/**
 * A part-to-whole bar: an ordered set of segments sized proportionally along
 * one track, headed by the total they divide and keyed by the legend that
 * names them.
 *
 * Colours come from the theme's categorical series scale, keyed to a segment's
 * own place in `segments` so a member that empties and refills keeps its colour
 * instead of recolouring every sibling beside it. On a 10px bar the fill is the
 * only channel the segment has, so the scale is the measured one rather than
 * the identity rotation, whose members are tinted carriers that sit 4.2 OKLab
 * ΔE apart once their ink is taken away. A segment that genuinely reports a
 * state pins its own tone through `color`.
 *
 * The legend is the visual/contextual reading merged with the graph. In a
 * selectable bar the painted segments themselves are native buttons: the
 * mark is the primary interaction surface, while the legend remains a
 * non-interactive explanation of the same marks. The legend is hidden from
 * assistive technology in that mode because each graph button already carries
 * the complete name, value and share. A passive bar keeps its accessible
 * reading in the legend, or on the segments when no legend is rendered.
 *
 * The zero case is a state, not an absence: with nothing to divide, the track
 * paints as one neutral run at its settled height rather than collapsing, so
 * the surface around it never reflows between "nothing yet" and "something".
 *
 * Every figure the bar prints has a slot of its own carrying today's rendering
 * as its default content — `figure-<segment.id>` for a key row's value, `total`
 * for the header sum — so a surface where a number opens its sources can make
 * them pressable without turning the key off and re-rendering it by hand. An
 * unfilled bar renders exactly what it always did.
 */
@schmancyElement('schmancy-segmented-bar')
export class SchmancySegmentedBar extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: block;
			}

			/* The painted mark is the control. Keep the state layer on the mark
			 * itself so a pointer never has to hunt for a separate key. */
			[part~='segment-control'] {
				transition:
					filter 150ms ease,
					box-shadow 150ms ease;
			}

			[part~='segment-control']:hover {
				filter: brightness(1.08);
			}

			[part~='segment-control']:active {
				filter: brightness(0.96);
			}
		`,
	]

	@property({ type: Array }) segments: SegmentedBarSegment[] = []

	/** Track height in px. */
	@property({ type: Number }) thickness = 10

	/**
	 * Renders the swatch/label/figure key beneath the track. On by default, and
	 * suppressed anyway below two drawn segments: identity carried by colour
	 * alone is unreadable, and one segment is already named by whatever titles
	 * the bar.
	 *
	 * THE KEY IS NOT DECORATION. Three of the series scale's six light steps sit
	 * under 3:1 against the light surface (2.68, 2.06, 2.57) — neighbours are
	 * told apart, but a segment is not a contrast-carrying mark on its own, so
	 * the scale is admissible only where every member also carries a VISIBLE
	 * label. That is what this default supplies, and turning it off moves the
	 * obligation onto the consumer rather than dissolving it: a bar with
	 * `legend=false` owes its reader the same names somewhere it renders itself
	 * (its own key, or the surrounding infographic). The `figure-<id>` slots
	 * below exist so keeping the key on costs a consumer nothing.
	 */
	@property({ type: Boolean }) legend = true

	/**
	 * Renders the header row: whatever the consumer slots into `header`, and the
	 * sum of every segment as the figure the bar is the breakdown of.
	 */
	@property({ type: Boolean }) total = true

	/**
	 * Makes the painted segments selectable. The track remains proportional, but
	 * each mark becomes the native control so the graph itself is the primary
	 * interaction surface; the legend stays a merged visual key.
	 */
	@property({ type: Boolean }) selectable = false

	/** Makes the painted segments interactive without giving them toggle
	 *  semantics. Use this for a mark that opens a drill; `selectable` is the
	 *  filter variant and adds `aria-pressed`/selected styling. */
	@property({ type: Boolean }) interactive = false

	/** Controlled identity of the selected graph member. */
	@property({ type: String, attribute: 'selected-id' }) selectedId: string | null = null

	@property({ attribute: false }) valueFormatter?: (v: number) => string

	@property({ attribute: false }) shareFormatter?: (share: number) => string

	/**
	 * What the neutral track means when there is nothing to divide — "nobody on
	 * the site", "no ticket retrieved yet". Without it the empty track is
	 * decorative and is hidden from assistive technology rather than announcing
	 * a split that does not exist.
	 */
	@property({ type: String }) emptyLabel = ''

	private formatValue(v: number): string {
		return this.valueFormatter?.(v) ?? String(v)
	}

	private formatShare(share: number): string {
		return this.shareFormatter?.(share) ?? `${Math.round(share * 100)}%`
	}

	private activateSegment(part: SegmentedBarPart, event: MouseEvent): void {
		this.dispatchEvent(
			new CustomEvent<SchmancySegmentActivateDetail>('segment-activate', {
				detail: {
					id: part.segment.id,
					segment: part.segment,
					index: part.index,
					originalEvent: event,
				},
				bubbles: true,
				composed: true,
			}),
		)
	}

	render() {
		// A zero or negative segment holds no part of the whole, so it is not a
		// sliver of the track and not a row of the legend either — it is absent
		// from the split, which is what its value says. Its SCALE slot is kept
		// all the same: the colour is taken from the segment's own position in
		// `segments`, so a segment that empties and refills keeps it instead of
		// re-colouring every sibling beside it.
		const total = this.segments.reduce((sum, segment) => sum + Math.max(0, segment.value), 0)
		const parts: SegmentedBarPart[] = this.segments
			.map((segment, index) => ({
				segment,
				index,
				share: segment.value / total,
				color: segment.color ?? index,
			}))
			.filter(part => part.segment.value > 0)
		const graphInteractive = this.selectable || this.interactive
		const keyed = this.legend && (parts.length >= 2 || (graphInteractive && parts.length === 1))

		return html`
			<div class="flex w-full flex-col gap-2">
				${when(
					this.total,
					() => html`
						<div part="header" class="flex items-baseline justify-between gap-3">
							<slot name="header"></slot>
							<slot name="total"
								><schmancy-typography type="label" token="md" weight="bold" class="shrink-0"
									>${this.formatValue(total)}</schmancy-typography
								></slot
							>
						</div>
					`,
				)}
				${when(
					parts.length === 0,
					() => html`
						<div
							part="track"
							class="w-full rounded-full bg-surface-containerHighest"
							style=${this.styleMap({ height: `${Math.round(this.thickness)}px` })}
							role=${this.emptyLabel ? 'img' : 'presentation'}
							aria-label=${this.emptyLabel}
							aria-hidden=${this.emptyLabel ? 'false' : 'true'}
						></div>
					`,
					() => html`
						<div
							part="track"
							class="flex w-full overflow-hidden"
							style=${this.styleMap({ height: `${Math.round(this.thickness)}px`, gap: `${SEGMENT_GAP_PX}px` })}
						>
							${repeat(
								parts,
								part => part.segment.id,
								part => html`
									${when(
																					graphInteractive,
																					() => html`
																						<button
																							part="segment segment-control"
																							type="button"
																							aria-label=${`${part.segment.label}: ${this.formatValue(part.segment.value)}, ${this.formatShare(part.share)}`}
																							aria-pressed=${ifDefined(
																							this.selectable ? (part.segment.id === this.selectedId ? 'true' : 'false') : undefined,
																						)}
																							title=${`${part.segment.label}: ${this.formatValue(part.segment.value)}, ${this.formatShare(part.share)}`}
																							class=${this.classMap({
													'h-full min-w-0 first:rounded-l-full last:rounded-r-full appearance-none border-0 p-0 text-left cursor-pointer focus:outline-solid focus:outline-2 focus:outline-offset-0 focus:outline-secondary-default': true,
													'ring-2 ring-inset ring-secondary-default':
														this.selectable && part.segment.id === this.selectedId,
												})}
																							style=${this.styleMap({
													flexGrow: String(part.segment.value),
													flexBasis: '0',
													backgroundColor: markRoles(part.color).ink,
												})}
																							@click=${(event: MouseEvent) => this.activateSegment(part, event)}
																							@keydown=${(event: KeyboardEvent) => {
															if (event.key !== 'Enter' && event.key !== ' ') return
															event.preventDefault()
															if (event.currentTarget instanceof HTMLButtonElement)
																event.currentTarget.click()
												}}
																						></button>
																					`,
																					() => html`
																						<div
																							part="segment"
																							class="h-full min-w-0 first:rounded-l-full last:rounded-r-full"
																							style=${this.styleMap({
													flexGrow: String(part.segment.value),
													flexBasis: '0',
													backgroundColor: markRoles(part.color).ink,
												})}
																							role=${keyed ? 'presentation' : 'img'}
																							aria-label=${keyed ? '' : `${part.segment.label}: ${this.formatValue(part.segment.value)}, ${this.formatShare(part.share)}`}
																							aria-hidden=${keyed ? 'true' : 'false'}
																						></div>
																					`,
																				)}
								`,
							)}
						</div>
					`,
				)}
				${when(
					keyed,
					() => html`
						<div
							part="legend"
							class="grid items-center gap-x-4 gap-y-1 grid-cols-[repeat(auto-fill,minmax(180px,1fr))]"
							aria-hidden=${ifDefined(graphInteractive ? 'true' : undefined)}
						>
							${repeat(
								parts,
								part => part.segment.id,
								part => html`
									<div part="legend-item" class="flex min-w-0 items-center gap-1.5" data-segment-id=${part.segment.id}>
										<schmancy-tone-dot class="h-2.5 w-2.5" .color=${part.color} variant="solid"></schmancy-tone-dot>
										<schmancy-typography type="label" token="sm" class="min-w-0 truncate text-surface-on-variant"
											>${part.segment.label}</schmancy-typography
										>
										<slot name=${`figure-${part.segment.id}`}
											><schmancy-typography type="label" token="sm" class="shrink-0"
												>${this.formatValue(part.segment.value)}</schmancy-typography
											></slot
										>
										<schmancy-typography type="label" token="xs" class="shrink-0 text-surface-on-variant"
											>${this.formatShare(part.share)}</schmancy-typography
										>
									</div>
								`,
							)}
						</div>
					`,
				)}
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-segmented-bar': SchmancySegmentedBar
	}
}
